import { useState, useCallback, useEffect, ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  Over,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import styles from './SortableList.module.css';
import { SortableListItem, Item } from './SortableListItem/SortableListItem';

type TProps = {
  items: string[];
  itemRenderer?: (
    params: { item: string; isDragging: boolean },
    index: number,
  ) => ReactNode;
  controlled?: boolean;
  onChange?: (items: string[]) => void;
};

export const SortableList = ({
  items: defaultItems = [],
  onChange,
  itemRenderer,
  controlled = true,
}: TProps) => {
  const [items, setItems] = useState<string[]>(defaultItems);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setItems((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf((over as Over).id as string);
        const newItems = arrayMove(prev, oldIndex, newIndex);

        onChange?.(newItems);
        return newItems;
      });
    }
    setActiveId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  useEffect(() => {
    if (controlled) {
      setItems(defaultItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled, JSON.stringify(defaultItems)]);

  return (
    <div className={styles.sortableList}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={items} strategy={rectSortingStrategy}>
          <div className={styles.sortableListGrid}>
            {items.map((item, ind) => (
              <SortableListItem
                key={item}
                item={item}
                ind={ind}
                itemRenderer={itemRenderer}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay adjustScale style={{ transformOrigin: '0 0 ' }}>
          {activeId ? (
            <Item
              ind={-1}
              item={activeId}
              isDragging
              itemRenderer={itemRenderer}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
